import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Building2, Handshake, PackageCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

const PERKS = [
  { icon: PackageCheck, title: "Precios de mayoreo", text: "Descuentos por volumen en todo el catálogo: salsas, cremas y condimentos." },
  { icon: Handshake, title: "Atención directa", text: "Tratas directamente con la familia. Sin intermediarios ni vueltas." },
  { icon: Building2, title: "Para tu negocio", text: "Restaurantes, tiendas gourmet, cafeterías y eventos en todo el sureste." },
];

export default function Mayoreo() {
  const [form, setForm] = useState({ name: "", business: "", email: "", phone: "", tipo: "frasco", products: "", quantity: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const idemKey = useRef(crypto.randomUUID());
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/quotes", { ...form, idempotency_key: idemKey.current });
      setSent(true);
      idemKey.current = crypto.randomUUID();
      if (data && data.email_sent === false) {
        toast.success("¡Cotización registrada! Te contactamos muy pronto.");
      } else {
        toast.success("¡Cotización enviada! Te contactamos muy pronto.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setLoading(false);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8" data-testid="mayoreo-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} data-testid="mayoreo-tables">
        <p className="text-center font-script font-semibold text-3xl text-orange">Descuentos oficiales por volumen</p>
        <h2 className="mt-2 text-center font-script text-3xl font-semibold text-cocoa sm:text-4xl">Precios de mayoreo</h2>
        <div className="mt-10 grid gap-8 lg:grid-cols-2">
          <div className="overflow-hidden rounded-2xl border border-sand bg-white" data-testid="tabla-mayoreo-frasco">
            <div className="bg-cocoa px-6 py-4">
              <h3 className="font-display text-xl font-bold text-cream">Mayoreo por frasco</h3>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-sand text-left text-clay">
                  <th className="px-6 py-3 font-semibold">Cantidad de frascos</th>
                  <th className="px-6 py-3 font-semibold">Descuento</th>
                  <th className="px-6 py-3 font-semibold">Cajas</th>
                </tr>
              </thead>
              <tbody>
                {[["60 – 192 frascos", "20%", "16"], ["193 – 300 frascos", "25%", "25"], ["301 – 500 frascos", "35%", "41"], ["501 – 1200 frascos", "40%", "100"], ["Más de 1201 frascos", "45%", "—"]].map((r) => (
                  <tr key={r[0]} className="border-b border-sand/60 last:border-0">
                    <td className="px-6 py-3 font-semibold text-cocoa">{r[0]}</td>
                    <td className="px-6 py-3"><span className="rounded-full bg-habanero/10 px-3 py-1 font-bold text-habanero">{r[1]}</span></td>
                    <td className="px-6 py-3 text-clay">{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="overflow-hidden rounded-2xl border border-sand bg-white" data-testid="tabla-mayoreo-litro">
            <div className="bg-habanero px-6 py-4">
              <h3 className="font-display text-xl font-bold text-white">Mayoreo por litro</h3>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-sand text-left text-clay">
                  <th className="px-6 py-3 font-semibold">Cantidad de litros</th>
                  <th className="px-6 py-3 font-semibold">Descuento</th>
                </tr>
              </thead>
              <tbody>
                {[["10 litros", "25%"], ["101 litros", "35%"], ["500 litros", "40%"], ["1001 litros", "45%"]].map((r) => (
                  <tr key={r[0]} className="border-b border-sand/60 last:border-0">
                    <td className="px-6 py-3 font-semibold text-cocoa">{r[0]}</td>
                    <td className="px-6 py-3"><span className="rounded-full bg-habanero/10 px-3 py-1 font-bold text-habanero">{r[1]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>
      <div className="mt-16 grid gap-12 lg:grid-cols-2">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
          <p className="font-script font-semibold text-3xl text-orange">Para negocios con buen gusto</p>
          <h1 className="mt-4 font-script text-4xl font-semibold text-cocoa sm:text-5xl text-balance">
            Mayoreo y cotizaciones B2B
          </h1>
          <p className="mt-6 max-w-md leading-relaxed text-clay md:text-lg">
            Si tienes restaurante, tiendita, cafetería o evento, llévate el sazón SoyDiVa con precios especiales por
            volumen. Llena el formulario y te armamos una cotización a la medida.
          </p>
          <div className="mt-10 space-y-6">
            {PERKS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex gap-4"
                data-testid={`mayoreo-perk-${i}`}
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-habanero/10 text-habanero">
                  <p.icon size={22} />
                </span>
                <div>
                  <h3 className="font-display text-lg font-bold text-cocoa">{p.title}</h3>
                  <p className="text-sm text-clay">{p.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          {sent ? (
            <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-sand bg-white p-12 text-center" data-testid="quote-success">
              <p className="font-script font-semibold text-4xl text-orange">¡Recibido!</p>
              <p className="mt-4 max-w-sm text-clay">
                Gracias por tu interés. Te contactaremos por correo o WhatsApp en menos de 48 horas con tu cotización.
              </p>
              <Button onClick={() => { setSent(false); setForm({ name: "", business: "", email: "", phone: "", tipo: "frasco", products: "", quantity: "", message: "" }); }} variant="outline" className="mt-8 rounded-full border-sand text-cocoa" data-testid="quote-another-button">
                Enviar otra cotización
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-sand bg-white p-8" data-testid="quote-form">
              <h2 className="font-script text-2xl font-semibold text-cocoa">Solicita tu cotización</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="q-name">Nombre de contacto *</Label>
                  <Input id="q-name" data-testid="quote-name-input" value={form.name} onChange={set("name")} className="mt-1 border-sand" required />
                </div>
                <div>
                  <Label htmlFor="q-business">Negocio</Label>
                  <Input id="q-business" data-testid="quote-business-input" value={form.business} onChange={set("business")} className="mt-1 border-sand" placeholder="Restaurante, tienda..." />
                </div>
                <div>
                  <Label htmlFor="q-email">Correo *</Label>
                  <Input id="q-email" type="email" data-testid="quote-email-input" value={form.email} onChange={set("email")} className="mt-1 border-sand" required />
                </div>
                <div>
                  <Label htmlFor="q-phone">Teléfono / WhatsApp *</Label>
                  <Input id="q-phone" data-testid="quote-phone-input" value={form.phone} onChange={set("phone")} className="mt-1 border-sand" required />
                </div>
                <div className="sm:col-span-2">
                  <Label>Me interesa cotizar</Label>
                  <div className="mt-2 flex gap-3">
                    {[["frasco", "Por frasco"], ["litro", "Por litro"]].map(([val, lab]) => (
                      <button
                        key={val}
                        type="button"
                        data-testid={`quote-tipo-${val}`}
                        onClick={() => setForm((f) => ({ ...f, tipo: val }))}
                        aria-pressed={form.tipo === val}
                        className={`rounded-full border px-5 py-2 text-sm font-bold transition-colors ${
                          form.tipo === val
                            ? "border-habanero bg-habanero text-white"
                            : "border-sand bg-white text-cocoa hover:border-habanero hover:text-habanero"
                        }`}
                      >
                        {lab}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="q-products">Productos de interés</Label>
                  <Input id="q-products" data-testid="quote-products-input" value={form.products} onChange={set("products")} className="mt-1 border-sand" placeholder="Ej. Salsa Macha, Crema Habanero..." />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="q-quantity">Cantidad estimada</Label>
                  <Input id="q-quantity" data-testid="quote-quantity-input" value={form.quantity} onChange={set("quantity")} className="mt-1 border-sand" placeholder="Ej. 120 frascos mensuales / 50 litros" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="q-message">Mensaje</Label>
                  <Textarea id="q-message" data-testid="quote-message-input" value={form.message} onChange={set("message")} className="mt-1 border-sand" rows={4} placeholder="Cuéntanos de tu negocio y qué necesitas..." />
                </div>
              </div>
              <Button type="submit" disabled={loading} data-testid="quote-submit-button" className="mt-6 w-full rounded-full bg-habanero py-6 font-bold text-white hover:bg-habanero-dark">
                <Send className="mr-2" size={16} /> {loading ? "Enviando..." : "Enviar solicitud de cotización"}
              </Button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
